<!-- eslint-disable vue/attribute-hyphenation -->
<template>
  <div data-app>
    <forum-context-site-header
      :message-search-url="headerInfo.messageSearchUrl"
      :site-name="headerInfo.siteName"
      :site-id="headerInfo.siteId"
      :has-site-notes="headerInfo.hasSiteNotes"
      :in-watchlist="siteInWatchlist"
      :site-url="siteUrl"
    />
    <div class="form-group search">
      <label for="search_forums">
        {{ gettext('Search sections:') }}
      </label>
      <div class="input-btn-group search-with-btn">
        <div class="search-bar">
          <autocomplete-input
              inputId="search"
              :initial-value="query"
              :suggestions="sectionNames"
              @getSuggestions="getSectionSuggestions"
              @submitInput="searchSections"
              @setInput="(newQuery) => query = newQuery"
          />
          <button
            ref="searchBtn"
            class="btn btn--primary btn--inline"
            @click.prevent="searchSections"
          >
            <img
              src="/static/images/icons/icon-search-white.svg"
              width="24"
              alt="Search"
              style="display:block"
            >
          </button>
        </div>
      </div>
    </div>
    <pagination-component
      itemName="Section"
      :loading="loading"
      :totalItems="totalForums"
      :initialParams="initialParams"
      :sortOptions="sortOptions"
      :sortUpdate="sortUpdate"
      :showNavOptions="true"
      :syncPaginationControls="performPaginationSync"
      @syncPaginationControls="syncPagination"
      @performSearch="fetchForumIndices"
    />
    <div
      v-if="loading"
      class="alert alert--info u-text-centre"
    >
      {{ gettext('Loading...') }}
    </div>
    <div
      v-if="!loading && totalForums > 0"
      class="site-index"
    >
      <notes-legend />
      <ul id="search-results">
        <li
          v-for="(forum, index) in forumsData"
          :key="index"
          class="site-index__row"
        >
          <p class="site-index__name">
            <a
              v-if="forum.site_name"
              :href="'/en/message-search/forum-sites/'+ forum.site_name + '/' + forum.forum"
            >
              {{ forum.forum }}
            </a>
            <span v-else>No Forum Name</span>
            <note-buttons
              :noteId="forum.forum_id"
              :noteName="forum.forum"
              :noteLinked="forum.uuid"
              :noteItem="'forum'"
              :noteSiteId="forum.site_id"
              :noteSiteName="forum.site_name"
              :hasNotes="forum.has_forum_notes"
              :indexType="'forum'"
              :siteLink="getForumLink(forum.site_name, forum.forum)"
              :watchlistMessage="{
                'forum_watchlist': forum.forum_watchlist,
                'url':forum.url,
                '_source': {
                  'site_id': forum.site_id,
                  'site_name': forum.site_name,
                  'forum': forum.forum,
                }
              }"
              @refresh="fetchForumIndices"
            />
          </p>
          <div class="site-index__info">
            <i class="u-fontsize-sm">
              Last post in section:
            </i>
            <br>
            <span v-if="forum.topic">
              in
              <a
                :href="'/en/message-search/?filters=site_name:'+ forum.site_name + ',topic:' + encodeURL(forum.topic_url_encoded)"
                class="link--search"
              >
                {{ forum.topic }}
              </a>
              <note-buttons
                :noteId="forum.topic_id"
                :noteName="forum.topic"
                :noteLinked="forum.uuid"
                :noteItem="'Topic'"
                :noteSiteId="forum.site_id"
                :noteSiteName="forum.site_name"
                :hasNotes="forum.has_topic_notes"
                :indexType="'forum'"
                :siteLink="getTopicLink(forum.site_name, forum.topic)"
                :watchlistMessage="{
                  'url':forum.url,
                  'topic_watchlist': forum.topic_watchlist,
                  '_source': {
                    'site_id': forum.site_id,
                    'site_name': forum.site_name,
                    'topic': forum.topic,
                    'site':forum.site
                  }
                }"
                @refresh="fetchForumIndices"
              />
            </span>
            <span v-else-if="forum.topic_id">
              <a
                :href="'/en/message-search/?filters=site_name:'+ forum.site_name + ',topic_id:' + forum.topic_id"
                class="link--search"
              >
                <i>(no topic name)</i>
              </a>
              <note-buttons
                :noteId="forum.topic_id"
                :noteName="forum.topic"
                :noteLinked="forum.uuid"
                :noteItem="'topic'"
                :noteSiteId="forum.site_id"
                :noteSiteName="forum.site_name"
                :hasNotes="forum.has_topic_notes"
                :indexType="'forum'"
                :siteLink="getTopicIdLink(forum.site_name, forum.topic_id)"
                @refresh="fetchForumIndices"
              />
            </span>
            <span v-else><i>(no topic name)</i></span>
            <br>
            by
            <a
              :href="'/en/message-search/?filters=site_name:'+ forum.site_name + ',username:' + encodeURL(forum.username_url_encoded)"
              class="link--search"
            >
              {{ forum.username }}
            </a>
            <note-buttons
              :noteId="forum.user_id"
              :noteName="forum.username"
              :noteLinked="forum.uuid"
              :noteItem="'user'"
              :noteSiteId="forum.site_id"
              :noteSiteName="forum.site_name"
              :hasNotes="forum.has_user_notes"
              :indexType="'forum'"
              :siteLink="getUserLink(forum.site_name, forum.username)"
              :watchlistMessage="{
                'url':forum.url,
                'username': forum.username,
                'watchlist': forum.watchlist,
                'site': {
                  'site_id': forum.site_id,
                  'site_name': forum.site_name
                }
              }"
              @refresh="fetchForumIndices"
            />
            <a
              class="u-pl-sm btn--sm"
              :href="`/en/user/detail/${forum.username}/${forum.site_name}`"
            >
              View Username Details
              <svg
                class="icon u-fill-current u-fontsize-sm"
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 100 100"
              >
                <path d="M47 2L35 14l27 27-40-1H6v20h16l40-1-27 27 12 12 48-48z" />
              </svg>
            </a>
            <br>
            {{ formatDate(forum.posted_date) }}
          </div>
        </li>
      </ul>
    </div>

    <div
      v-if="!loading && totalForums === 0"
    >
      <p class="alert alert--error">
        No sections found
      </p>
    </div>

    <pagination-component
      itemName="Section"
      :loading="loading"
      :isPrimary="false"
      :totalItems="totalForums"
      :syncPaginationControls="performPaginationSync"
      @syncPaginationControls="syncPagination"
      @performSearch="fetchForumIndices"
    />
  </div>
</template>

<script>
import AutocompleteInput from './AutocompleteInput'
import NoteButtons from './notes/NoteButtons'
import ForumContextSiteHeader from './forum_search/ForumContextSiteHeader'
import PaginationComponent from './PaginationComponent'
import NotesLegend from './notes/NotesLegend'
import axios from 'axios'
import Cookies from 'js-cookie'

export default {
  name: 'ForumIndex',
  components: {
    AutocompleteInput,
    ForumContextSiteHeader,
    NoteButtons,
    NotesLegend,
    PaginationComponent,
  },
  props: {
    siteName: {
      type: String,
      required: true
    },
    headerInfo: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      forumsData: [],
      siteUrl: '',
      siteInWatchlist: false,
      loading: true,
      totalForums: 0,
      initialParams: {
        start: 0,
        limit: 25,
        sort: '-posted_date'
      },
      query: '',
      sectionNames: [],
      sortOptions: {
        '-posted_date': 'Most Recent',
        '+forum': 'Alphabetical',
      },
      sortUpdate: null,
      performPaginationSync: false,
    }
  },
  computed: {
    forumIndexURL() {
      return `/en/indices/${this.siteName}/forum_index/`
    }
  },
  mounted() {
    // data retrieval request is emitted by pagination component
    let searchParams = new URLSearchParams(window.location.search)
    if (searchParams.get('query') != null && searchParams.get('query') !== '') {
      this.query = searchParams.get('query')
      this.sortOptions['+relevance'] = 'Relevance'
    }
  },
  methods: {
    syncPagination(performSync) {
      this.$nextTick(() => {
        this.performPaginationSync = performSync
      })
    },
    getForumLink(siteName, forum) {
      return new URL('/en/message-search/forum-sites/' + siteName + '/' + forum + '/', document.baseURI).href
    },
    getTopicLink(siteName, topic) {
      return new URL('/en/message-search/?filters=site_name:' + siteName + ',topic:' + this.encodeURL(topic), document.baseURI).href
    },
    getTopicIdLink(siteName, topicId) {
      return new URL('/en/message-search/?filters=site_name:' + siteName + ',topic_id:' + topicId, document.baseURI).href
    },
    getUserLink(siteName, userName) {
      return new URL('/en/message-search/?filters=site_name:' + siteName + ',username:' + userName, document.baseURI).href
    },
    encodeURL(topic) {
      return encodeURIComponent(topic)
    },
    formatDate(date) {
      const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }
      return new Date(date * 1000).toLocaleString('en-US', options)
    },
    fetchForumIndices() {
      this.loading = true

      let searchParams = new URLSearchParams(window.location.search)
      // unfortunately we need to convert search params object into a regular js
      // object so it may be passed into request
      let requestParams = {}
      for (const [key, value] of searchParams) {
        requestParams[key] = value
      }

      const csrftoken = Cookies.get('csrftoken')
      axios.get(this.forumIndexURL, {'headers': {'X-CSRFToken': csrftoken}, 'params': requestParams})
        .then(response => {
          this.forumsData = response.data.data
          this.totalForums = response.data.total
          if (this.forumsData.length > 0) {
            this.siteInWatchlist = this.forumsData[0].site_watchlist
            this.siteUrl = this.forumsData[0].url
          }
        })
        .catch(error => {
          console.log(error)
          this.forumsData = []
          this.totalForums = 0
        }).finally(() => {
          this.loading = false
        })
    },
    getSectionSuggestions(term) {
      this.query = term
      axios.get(
          '/en/autocomplete/forum_sections/',
          {
            'params': {
              'site_name': this.siteName,
              'term': term,
            }
          }
      ).then((response) => {
        this.sectionNames = response.data
      })
    },
    searchSections() {
      let url = new URL(window.location)
      url.searchParams.set('start', 0)
      url.searchParams.set('query', this.query)
      if (this.query != null && this.query !== '') {
        this.sortOptions['+relevance'] = 'Relevance'
        url.searchParams.set('sort', '+relevance')
        this.sortUpdate = '+relevance'

      } else {
        if (url.searchParams.get('sort') === '+relevance') {
          url.searchParams.set('sort', '-posted_date')
          this.sortUpdate = '-posted_date'
        }
        delete this.sortOptions['+relevance']
      }
      window.history.pushState({}, '', url)
      this.fetchForumIndices()
      this.performPaginationSync = true
    }
  }
}
</script>

<style scoped>
.search-with-btn button {
  vertical-align: top;
  padding-top: 0.4em;
  padding-bottom: 0.4em;
  border: 2px solid #c22c3f;
}
.search-bar {
  display: flex;
  justify-items: stretch;
  gap: 5px;
  .autocomplete {
    width: calc(100% - 78px);
  }
}
.search {
  margin-top: 1.25rem;
  max-width: 50rem;
  margin-left: auto;
  margin-right: auto;
}
</style>
